import React, { useState } from 'react'
import styled from 'styled-components'
import { Dropdown, MenuProps } from 'antd'
import { FileTextOutlined, EllipsisOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { procedureConstants } from 'locale/procedures'
import {
  ListItem,
  EmptyState,
  Spinner,
  NavigationModal,
  Heading
} from '@lmc/ui-kit'
import { Procedure } from '../types'
import {
  formatDate,
  formatTime,
  truncateDescription
} from '../utils/procedureUtils'
import { useProcedureArchive } from '../hooks/useProcedureArchive'
import useRoleAccess from '../../../../../components/AccessControl/useRoleAccess'
import { UserRole } from '@lmc/ts-common'

interface ProcedureListProps {
  procedures: Procedure[]
  isLoading: boolean
  onEdit: (procedure: Procedure) => void
  onRefetch: () => void
}

export const ProcedureList: React.FC<ProcedureListProps> = ({
  procedures,
  isLoading,
  onEdit,
  onRefetch
}) => {
  const { t } = useTranslation(NAMESPACE.PROCEDURES)
  const [expandedProcedureId, setExpandedProcedureId] = useState<string | null>(
    null
  )
  const [archiveModalVisible, setArchiveModalVisible] = useState(false)
  const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(
    null
  )

  const { archiveProcedure, unarchiveProcedure, isArchiving } =
    useProcedureArchive(() => {
      onRefetch()
      setArchiveModalVisible(false)
      setSelectedProcedure(null)
    })

  const hasEditAccess = useRoleAccess([
    UserRole.lmcAdmin,
    UserRole.careHomeAdmin,
    UserRole.seniorCarer
  ])

  if (isLoading) {
    return <Spinner />
  }

  if (procedures.length === 0) {
    return (
      <EmptyState
        title={t(procedureConstants.NO_PROCEDURES_FOUND)}
        description={t(procedureConstants.NO_PROCEDURES_DESCRIPTION)}
      />
    )
  }

  const activeProcedures = procedures.filter((p) => p.status === 'active')
  const archivedProcedures = procedures.filter((p) => p.status === 'archived')

  const handleView = (procedure: Procedure) => {
    if (expandedProcedureId === procedure._id) {
      setExpandedProcedureId(null)
    } else {
      setExpandedProcedureId(procedure._id)
    }
  }

  const handleArchiveClick = (procedure: Procedure) => {
    setSelectedProcedure(procedure)
    setArchiveModalVisible(true)
  }

  const handleConfirmArchive = async () => {
    if (!selectedProcedure) return

    if (selectedProcedure.status === 'active') {
      await archiveProcedure(selectedProcedure._id)
    } else {
      await unarchiveProcedure(selectedProcedure._id)
    }
  }

  const getMenuItems = (procedure: Procedure): MenuProps['items'] => {
    const items: MenuProps['items'] = [
      {
        key: 'view',
        label: t(procedureConstants.VIEW),
        onClick: () => handleView(procedure)
      }
    ]

    if (hasEditAccess) {
      items.push(
        {
          key: 'edit',
          label: t(procedureConstants.EDIT),
          onClick: () => onEdit(procedure)
        },
        {
          key: 'archive',
          label:
            procedure.status === 'active'
              ? t(procedureConstants.ARCHIVE)
              : t(procedureConstants.UN_ARCHIVE),
          onClick: () => handleArchiveClick(procedure)
        }
      )
    }

    return items
  }

  const renderProcedure = (procedure: Procedure) => {
    const dateTimeText = [
      formatDate(procedure.procedureDate),
      formatTime(procedure.procedureTime)
    ]
      .filter(Boolean)
      .join(' at ')

    const carerName = procedure.updatedBy?.name || procedure.createdBy.name

    const isExpanded = expandedProcedureId === procedure._id

    return (
      <ProcedureItemContainer key={procedure._id}>
        <ListItem
          iconContent={<StyledFileTextOutlined />}
          titleContent={<Title>{procedure.title}</Title>}
          subTitleContent={
            <div>
              <MetadataRow>
                <Subtitle>
                  {dateTimeText || t(procedureConstants.NO_DATE_TIME_SPECIFIED)}
                </Subtitle>
                <Bullet>•</Bullet>
                <Subtitle>{carerName}</Subtitle>
              </MetadataRow>
              <Description>
                {truncateDescription(procedure.description)}
              </Description>
            </div>
          }
          rightChildren={
            <Dropdown
              menu={{ items: getMenuItems(procedure) }}
              trigger={['click']}
            >
              <ActionButton>
                <EllipsisOutlined style={{ fontSize: 20 }} />
              </ActionButton>
            </Dropdown>
          }
        />
        {isExpanded && procedure.description && (
          <FullDescription>{procedure.description}</FullDescription>
        )}
      </ProcedureItemContainer>
    )
  }

  return (
    <>
      <ListContainer>
        {activeProcedures.map(renderProcedure)}

        {archivedProcedures.length > 0 && (
          <>
            <SectionHeading>
              <Heading level={4}>
                {t(procedureConstants.ARCHIVED_PROCEDURES)}
              </Heading>
            </SectionHeading>
            {archivedProcedures.map(renderProcedure)}
          </>
        )}
      </ListContainer>

      <NavigationModal
        showPrompt={archiveModalVisible}
        confirmNavigation={handleConfirmArchive}
        cancelNavigation={() => {
          setArchiveModalVisible(false)
          setSelectedProcedure(null)
        }}
        modalTitle={
          selectedProcedure?.status === 'active'
            ? t(procedureConstants.ARCHIVE_PROCEDURE)
            : t(procedureConstants.RESTORE_PROCEDURE)
        }
        modalContentText={
          selectedProcedure?.status === 'active'
            ? t(procedureConstants.ARCHIVE_CONFIRMATION)
            : t(procedureConstants.RESTORE_CONFIRMATION)
        }
        modalConfirmText={
          selectedProcedure?.status === 'active'
            ? t(procedureConstants.ARCHIVE)
            : t(procedureConstants.RESTORE)
        }
        modalCancelText={t(procedureConstants.CANCEL)}
      />
    </>
  )
}

const StyledFileTextOutlined = styled(FileTextOutlined)`
  font-size: 20px;
`

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 24px auto 0px auto;
  padding-bottom: 40px;
  max-width: 1080px;
`

const ProcedureItemContainer = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
`

const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #333;
  margin-bottom: 4px;
`

const MetadataRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

const Subtitle = styled.div`
  font-size: 14px;
  color: #666;
`

const Bullet = styled.span`
  font-size: 14px;
  color: #666;
`

const Description = styled.div`
  font-size: 14px;
  color: #555;
  line-height: 1.5;
`

const FullDescription = styled.div`
  font-size: 14px;
  color: #555;
  line-height: 1.5;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 4px;
  margin-top: 12px;
`

const ActionButton = styled.div`
  cursor: pointer;
  padding: 8px;

  &:hover {
    background: #f0f0f0;
    border-radius: 4px;
  }
`

const SectionHeading = styled.div`
  margin-top: 32px;
  margin-bottom: 16px;
`
